import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import i18n from '../../i18n';
import {withFormik, useFormikContext} from 'formik';
import {withStyles} from '@material-ui/core';
import {mergeWithDefault} from '../Form/utils';
import swal from "sweetalert";
import {object, string, number, date} from 'yup';
import FormFields from './FormFields';
import MediaDropButton from '../Media/DropButton';
import Divider from '@material-ui/core/Divider';
import _ from 'lodash';
import attachmentReferenceMutations from '../../mutations/attachmentReferenceMutations';
import projectMutations from '../../mutations/projectMutations';
import moment from 'moment';
import noteMutations from '../../mutations/noteMutations';
import {getMutationName} from '../Form/utils';
import SaveEvent from '../SaveShortcut/FunctionComponent';
import projectContactMutations from "../../mutations/projectContactMutations";
import projectBuildingMutations from "../../mutations/projectBuildingMutations";
import apartmentMutations from "../../mutations/apartmentMutations";
import {gql} from "apollo-boost";
import PDFDocument from "../File/PDF/PDFDocument";
import FileRenderer from "../File/PDF/FileRenderer";
import PDFAddendumDialog from "./Dialogs/PDFAddendumDialog";
import SwalList from "../Helper/SwalList";
import attachmentQueries from "../../queries/attachmentQueries";

const saveAttachment = async(fileData, project_id) => {

    const query = attachmentQueries.findAttachmentReference;
    const queryName = getMutationName(query);
    const variables = {id:fileData.id}

    const{data:reference} = await window.apolloClient.query({ query, variables, errorPolicy: "all"});
    const data = reference[queryName] || null;
    const attachmentReferences = data['attachmentReferences'] || null;
    const fileId = data.file_id;

    // check if project has reference to this file
    const referenceExists = attachmentReferences &&
        attachmentReferences.length &&
        attachmentReferences.find(ref => (
            ref.project_id !== null &&
            ref.project_id === parseInt( project_id ) &&
            fileId === fileData.sHash
        ));

    if(!referenceExists) {
        try {
            const mutation = attachmentReferenceMutations.createAttachmentReference;
            const {data: result} = await window.apolloClient.mutate({mutation, variables: { project_id: parseInt( project_id ) , attachment_id: fileData.id}, errorPolicy: "all"});

            return result['createAttachmentReference'];
        } catch(e) {
            return e;
        }
    }
};

const saveMainNote = async (note_content, note_id) => {
    const mutation = noteMutations.createNote; // create new every time, id only for history!
    const queryName = getMutationName(mutation);
    const {data: result} = await window.apolloClient.mutate({mutation, variables: {
        content: note_content,
        creator_id: window.__DATA__._user.id,
    }});

    return result[queryName];
};

const manipulateValuesBeforeSave = async (values) => {
    if (!values.internal) {
        // if the project is editing then generated code is empty and here transfers it to code
        // that's why code will be used instead of empty generated_code
        values.code = values.generated_code || values.code; // if the project code is not internal then using auto generated code
    }

    /*if (values.note_content.length) {
        values.note = await saveMainNote(values.note_content, values.note ? values.note.id : null);
    }
    values.note_id = values.note ? values.note.id : null;*/

    ['client_note', 'deadline', 'first_grace_period','second_grace_period','reminder','defects_eliminated'].forEach((item) => {
        if ( values[item] === '' ) {
            values[item] = null;
        } else {
            values[item] = moment(values[item]).format('YYYY-MM-DD HH:mm:ss');
        }
    });
    values["updated_at"] = moment().format('YYYY-MM-DD HH:mm:ss');

    return values;
};

const saveApartment = async(values) => {
    if(values.apartment_number || values.storey || values.tenant_name || values.tenant_phone_number) {
        const mutation = values.apartment_id > 0 ? apartmentMutations.updateApartment : apartmentMutations.createApartment;
        const queryName = getMutationName(mutation);
        const {data: result} = await window.apolloClient.mutate({mutation, variables: {
                id: values.apartment_id,
                number: values.apartment_number || null,
                storey: values.storey || null,
                tenant_name: values.tenant_name || null,
                tenant_phone_number: values.tenant_phone_number || null,
                editor_id: window.__DATA__._user.id,
            },errorPolicy: "all"});
        return result[queryName];
    } else {
        return null;
    }
}

const saveProjectBuildings = async (projectId,values) => {
    const mutation = values.building_id > 0 ? projectBuildingMutations.updateProjectBuilding : projectBuildingMutations.createProjectBuilding;
    const queryName = getMutationName(mutation);
    const {data: result} = await window.apolloClient.mutate({mutation, variables: {
            id: values.building_id || null,
            project_id: projectId || null,
            building_id: values.building_child_id || null,
            apartment_id: values.apartment_id || null,
            notice: values.notice || null,
    },errorPolicy: "all"});
    return result[queryName];
};

const saveSubContractor = async(projectId,values) => {

    const mutation = values.subcontractor_id > 0 ? projectContactMutations.updateProjectContact : projectContactMutations.createProjectContact;
    const queryName = getMutationName(mutation);
    const {data: result} = await window.apolloClient.mutate({mutation, variables: {
            id: values.subcontractor_id || null,
            project_id: projectId || null,
            contact_id: values.contact_id || null,
            description: 'subcontractor',
        },errorPolicy: "all"});
    return result[queryName];
};

const getBuildingId = async(newBuildingName,parentBuildingId) => {

    let queryName = 'findBuilding';

    const data = await window.apolloClientQuery(
        gql `{
            ${queryName}(
                name : "${ newBuildingName }"
                parent_id: ${ parentBuildingId }
            ) {
                id
            }
        }`
    );

    return data[queryName] ? data[queryName].id : null;
};

const saveProject = async ({files, ...values}) => {
    const mutation = values.id > 0 ? projectMutations.updateProject : projectMutations.createProject;
    let data = await manipulateValuesBeforeSave(values);

    let newBuildingId = await getBuildingId(values.building_child_name, parseInt( values.building_parent_id ) );
    data.building_child_id = newBuildingId || data.building_child_id;

    const {data: result} = await window.apolloClient.mutate({mutation, variables: {...data},errorPolicy: "all"});

    const queryName = getMutationName(mutation);
    const projectId = _.get(result, queryName + '.id');

    const apartment = await saveApartment(values);
    values.apartment_id = apartment && apartment.id;

    if(values.building_parent)  {
        await saveProjectBuildings(projectId,values);
    }

    const subcontractor = await saveSubContractor(projectId,values);

    if (files && files.length > 0) {
        for (const file of files) {
            await saveAttachment(file, projectId)
        }
    }

    return result[queryName];
};

// submits the form again after rendering and saving pdf
const AutoSubmit = () => {
    const { values, submitForm } = useFormikContext();
    useEffect(() => {
        if (values.rendered) {
            submitForm().then();
        }
    }, [values, submitForm]);
    return null;
};

const yupLabel = (name) => {
    return i18n.get('project.' + name);
};

const ProjectForm = (props) => {
    const {
        values,
        errors,
        touched,
        setFieldValue,
        handleChange,
        handleBlur,
        handleSubmit,
        uploadConfig,
        apartment,
        defaultStatus,
        classes,
        isSubmitting,
    } = props;

    // requesting procedure ...
    const [requesting,setRequesting] = useState(false);
    const [rendered,setRendered] = useState(false);
    const [isComplete,setIsComplete] = useState(false);

    const requestState = e => setRequesting(e);
    const renderState = e => setRendered(e);

    useEffect(() => {
        if(!requesting) {
            renderState(false);
        }
    },[requesting]);

    // gets new lines from addendum input
    const [addendum,setAddendum] = useState(false);
    const changeAddendum = text => setAddendum(text);

    useEffect(() => {
        if(addendum) {
            setFieldValue('correspondence',addendum);
            props.setSubmitting(true);
        }
    },[addendum])

    // state parameter for opening/closing addendum dialog
    // (requesting user for submitting additional correspondences)
    const [open, setOpen] = useState(false);
    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = (cancel) => {
        if(!cancel && !values.correspondence) {
            values.correspondence = [];
        }
        if(values.correspondence) {
            props.setSubmitting(true);
        }
        setOpen(false);
    };

    SaveEvent({handleSubmit});

    // needed for checking in formik.handleSubmit
    if(!isSubmitting) {
        values.submitting = false;
    }

    return (
        <form onSubmit={handleSubmit} >
            <FormFields {...props} handleOpen={handleOpen} requestState={requestState} isComplete={isComplete}>

                <Divider className={classes.divider}/>

                {uploadConfig && (
                    <React.Fragment>
                        <MediaDropButton
                            uploadConfig={uploadConfig}
                            entity={{label:'project_id', id: values.id }}
                            onNewFile={(file) => {
                                values.files.push(file);
                                setFieldValue('files', values.files);
                            }}
                            onFileRemoved = { (fileToRemove) => {
                                values.files = values.files.filter(
                                    file => file.iId !== fileToRemove.iId
                                )
                                setFieldValue('files', values.files);
                            }}
                        />
                        <Divider className={classes.divider}/>
                    </React.Fragment>
                )}

                {requesting && !rendered && !isComplete && (
                    <FileRenderer
                        requesting={requesting}
                        renderState={renderState}
                        document={<PDFDocument values={values} />}
                        onRendered={(file)=>{
                            setIsComplete(true);
                            values.files.push(file);
                            values.rendered = { id:file.id, name:file.name };
                            requestState(false);
                        }}
                        {...props}
                    />
                )}

                <PDFAddendumDialog
                    values={values || {}}
                    uploadConfig={uploadConfig}
                    changeAddendum={changeAddendum}
                    open={open}
                    handleClose={handleClose}
                />
            </FormFields>
            {(values.rendered && !requesting && !rendered && !isSubmitting) && (<AutoSubmit  />)}
        </form>
    );
};

const FormConfig = withFormik({
    mapPropsToValues: props => mergeWithDefault(props.values, {
        id: '',
        name: '',
        number_internal: '',
        number_client: '',
        code: '',
        generated_code: '',
        internal: false,
        subtitle: '',
        tags: '',
        // #apartment
        apartment_number:'',
        storey:'',
        tenant_name:'',
        tenant_phone_number:'',
        // #relations
        parent_id: '',
        parent: null,
        status_id: props.defaultStatus.id,
        status: props.defaultStatus,
        owner_id: window.__DATA__._user.id, // editor of deficiency
        owner: null,
        contact_id:'',
        contact:null,
        emails:null,
        subcontractor_id: '',
        apartment_id:'',
        building_id:'',
        building_parent_id: '',
        building_parent:null,
        building_child_id: '',
        building_child_name: '',
        building_child_address: '',
        building_child_trashed: '',
        note_id: '',
        note: null,
        note_content: '',
        files: [],
        correspondence:null,
        submitting:false,
        order_withdrawal: false,

        // #dates
        client_note:'',
        deadline: '',
        reminder:'',
        first_grace_period:'',
        second_grace_period:'',
        defects_eliminated: '',
    }),
    validationSchema: () => {
        let rules = {
            building_parent_id: number().label(yupLabel('deficiency.construction_project')).required(),
            building_child_id: number().label(yupLabel('deficiency.building')).required(),
            number_client: string().label(yupLabel('deficiency.number_client')).required(),
            name: string().label(yupLabel('deficiency.name')).min(3).required(),
            contact_id: number().label(yupLabel('deficiency.subcontractor')).required(),
            status_id: number().label(yupLabel('status')).required(),
            owner_id: number().label(yupLabel('owner')).required(),
            client_note: string().label(yupLabel('deficiency.date.client_note')).required(),
            ////change_note: string().label(yupLabel('change_note')).required(),
            reminder: date().when(['client_note','deadline','first_grace_period','second_grace_period'], (client_note, deadline, first_grace_period, second_grace_period, schema) => {
                if(second_grace_period) {
                    //if( moment().isBefore(second_grace_period) ){
                        //return schema.label(yupLabel('deficiency.date.reminder')).min(new Date()).max(second_grace_period);
                    //}
                } else if(first_grace_period) {
                    //return schema.label(yupLabel('deficiency.date.reminder')).min(new Date()).max(first_grace_period);
                } else if(deadline) {
                    //return schema.label(yupLabel('deficiency.date.reminder')).min(new Date()).max(deadline);
                } else if(client_note){
                    return schema.label(yupLabel('deficiency.date.reminder')).min(new Date()).max(moment(client_note).add(30, 'days').format('YYYY-MM-DD HH:mm:ss'));
                } else {
                    return schema;
                }
            }),
            //apartment_number: string().label(yupLabel('deficiency.apartment_number')).required(),
            //storey: string().label(yupLabel('deficiency.storey')).required(),
            //tenant_name: string().label(yupLabel('deficiency.tenant.name')).required(),
            //tenant_phone_number: string().label(yupLabel('deficiency.tenant.phone_number')).required(),
        };
        return object().shape(rules);
    },
    handleSubmit: (values, { setSubmitting }) => {

        let isDate = [values.deadline,values.first_grace_period,values.second_grace_period]
            .map(item => !!item).includes(true);

        if(values.submitting && isDate && !values.save_only) {
            setSubmitting(false);
        } else {
            setSubmitting(true);

            let messages = [];

            if(isDate && !values.save_only) {
                values.rendered && messages.push(
                    i18n.get('deficiency.file_rendered_and_saved',{ file: values.rendered.name })
                );
                messages.push(
                    i18n.get('deficiency.form_will_be_saved')
                );
                if(values.status.event !== 'second_grace_period') {
                    messages.push(
                        i18n.get('deficiency.schedule_notification',{date: moment(values.reminder).format('DD.MM.YYYY') } )
                    );
                }
                if(values.contact) {
                    if(values.contact.email) {
                        let emails = values.key_contacts
                            ? values.key_contacts
                            : values.contact.email;

                        messages.push(
                            i18n.get('deficiency.email_with_attachment_will_sent_to'),
                            `NU: ${values.contact.company_name}`,
                            `E-Mail: ${emails}`,
                        );
                    } else {
                        messages.push(
                            i18n.get('deficiency.email.no_address'),
                            i18n.get('deficiency.email.no_mailing'),
                        );
                    }
                }
            } else {
                messages.push(i18n.get('deficiency.form_will_be_saved'));
            }

            // renders a list in swal
            let wrapper = document.createElement('div');
            ReactDOM.render(<SwalList values={{messages:messages}} />, wrapper);
            let content = wrapper.firstChild;

            swal({
                content: content,
                icon: isDate ? 'success' : 'info',
                button: {
                    text: "ok",
                    closeModal: false,
                },
                closeOnClickOutside: !isDate,
                closeOnEsc: !isDate,
            }).then(res => {
                if(res) {
                    saveProject(values).then(data => {
                        // console.debug({data});
                        return swal({
                            text: i18n.get('deficiency.saved_data'),
                            icon: 'success',
                            timer: 4000,
                        }).then(() => {
                            delete values.rendered;
                            setSubmitting(false);
                            if (data.id > 0) {
                                window.location.href = document.referrer; // `/project`;
                            }
                        });
                    });
                } else {
                    setSubmitting(false);
                }
            });
        }
    },
    enableReinitialize:true,
    validateOnBlur: true,
    validateOnMount: true,
})(ProjectForm);

const styles = theme => ({
    deleteButton: {
        // background: theme.colors.error
    },
    sub_project: {
        marginTop: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 2,
    },
    divider: {
        marginTop: theme.spacing.unit * 2,
    },
    createButton: {
        minWidth:0,
    },
});

export default withStyles(styles)(FormConfig);
